<template>
  <div
    class="sme-file-input"
    @dragover.prevent="fileDragged = true"
    @dragleave.prevent="fileDragged = false"
    @drop.prevent="handleFileDrop"
  >
    <label class="sme-file-input__label" :class="{ 'sme-file-input--dragged': fileDragged }" :for="id">
      <input :id="id" type="file" :accept="acceptedFileTypes.join(', ')" @change="handleFileUpload" />

      <template v-if="file">
        <div>
          {{ file.name }}
          <div class="progress-section">
            <div class="progress-bar-container">
              <div class="progress-bar" :style="{ width: progress + '%' }"></div>
            </div>
            <div class="progress-percent">{{ progress }}%</div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="drop-space">
          <font-awesome-icon :icon="['fal', 'cloud-upload']" class="sme-file-input__icon" />
          <span class="d-block" v-safe-html="uploadText"></span>
          <small>
            {{
              $t('bulkUpload.supportedFileTypes', {
                extensions: acceptedFileTypes.map(fileType => FILE_TYPE_EXTENSIONS[fileType]).join(', '),
              })
            }}
          </small>
        </div>
      </template>
    </label>
  </div>
</template>

<script>
export const FILE_TYPES = {
  CSV: 'text/csv',
  XLS: 'application/vnd.ms-excel',
  XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

export const FILE_TYPE_EXTENSIONS = {
  [FILE_TYPES.CSV]: 'csv',
  [FILE_TYPES.XLS]: 'xls',
  [FILE_TYPES.XLSX]: 'xlsx',
};
</script>

<script setup>
import { ref } from 'vue';

defineProps({
  file: File,
  acceptedFileTypes: {
    type: Array,
    validator: acceptedFileTypes => acceptedFileTypes.every(fileType => Object.values(FILE_TYPES).includes(fileType)),
  },
  id: {
    type: String,
    required: true,
  },
  uploadText: {
    type: String,
    default: 'Drop file here or <u>click</u> to browse',
  },
});

const emit = defineEmits(['change']);

const fileDragged = ref(false);

const progress = ref(0);
const loading = ref(false);

const progressBar = () => {
  const interval = setInterval(() => {
    if (progress.value < 100) {
      progress.value += 5;
    } else {
      loading.value = false;
      clearInterval(interval);
    }
  }, 100);
};

const handleFileDrop = event => {
  fileDragged.value = false;

  handleFileUpload(event);
};

const handleFileUpload = event => {
  progressBar();

  emit('change', event);
};
</script>

<style lang="scss" scoped>
.sme-file-input__label {
  align-items: center;
  background-color: var(--palette-color-base-white);
  border: 1px dashed var(--palette-color-default-lighten-80);
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  justify-content: center;
  margin: 1rem 0;
  padding: 3rem 1.5rem;
  transition: background-color 0.25s;

  input[type='file'] {
    display: none;
  }
}

.sme-file-input--dragged {
  background-color: var(--palette-color-base-white);

  .sme-file-input__icon {
    color: var(--palette-color-brand-primary);
  }
}

.sme-file-input__icon {
  height: 2rem;
  margin-right: 1rem;
  transition: color -0.25s;
  width: 2rem;
}

.drop-space {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-direction: column;
}

.d-block {
  ::v-deep u {
    color: var(--palette-color-brand-primary);
  }
}

.progress-bar-container {
  width: 100%;
  height: 10px;
  background-color: var(--palette-color-default-lighten-90);
  border-radius: 5px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background-color: var(--palette-color-success);
  width: 0;
  transition: width 0.1s linear;
}

.progress-section {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.progress-percent {
  color: var(--palette-color-success);
}
</style>
